import { LibraryBig, Unlink } from "lucide-react";
import { useMemo, useState } from "react";
import { useStore } from "../../app/store.ts";
import { AUTO_SMOOTH_ANGLE_DEFAULT } from "../../modeling/ops/normals.ts";
import type { MaterialSlot, ShadingMode } from "../../modeling/types.ts";
import {
  FACE_SHADING_FLAT,
  FACE_SHADING_INHERIT,
  FACE_SHADING_SMOOTH,
  getFaceShading,
} from "../../modeling/types.ts";
import { Button, IconButton } from "../Button.tsx";
import { ColorPicker } from "../ColorPicker.tsx";
import { MaterialGalleryModal } from "../materialGallery/MaterialGalleryModal.tsx";
import { SidebarCard } from "../SidebarCard.tsx";
import {
  ButtonControlGroup,
  CompactRow,
  EyeToggle,
  ListControl,
  SectionLabel,
  SegmentedControl,
  SelectSearch,
  SliderRow,
} from "../shared.tsx";
import {
  COLOR_BORDER,
  COLOR_TEXT_FAINT,
  COLOR_TEXT_SUBTLE,
  FONT_SIZE_TINY,
  RADIUS_DOT,
} from "../tokens.ts";
import { MaterialPropertiesEditor } from "./MaterialPropertiesEditor.tsx";

const SHADING_OPTIONS: { label: string; value: ShadingMode }[] = [
  { label: "Flat", value: "flat" },
  { label: "Smooth", value: "smooth" },
  { label: "Auto", value: "autoSmooth" },
];

type FaceShadingMode = "inherit" | "flat" | "smooth";

const FACE_SHADING_OPTIONS: { label: string; value: FaceShadingMode }[] = [
  { label: "Flat", value: "flat" },
  { label: "Smooth", value: "smooth" },
  { label: "Inherit", value: "inherit" },
];

export function AppearanceSection({
  objectId,
  isSceneObject,
  bare,
}: {
  objectId: string;
  isSceneObject: boolean;
  bare?: boolean;
}) {
  const renderVisible = useStore(
    (s) => s.renderVisibility.get(objectId) ?? true,
  );
  const setRenderVisible = useStore((s) => s.setRenderVisible);
  const shading = useStore((s): ShadingMode => {
    if (isSceneObject) return s.sceneObjects.get(objectId)?.shading ?? "smooth";
    return s.shadingOverrides.get(objectId) ?? "smooth";
  });
  const autoSmoothAngle = useStore(
    (s) =>
      s.sceneObjects.get(objectId)?.autoSmoothAngle ??
      AUTO_SMOOTH_ANGLE_DEFAULT,
  );
  const setShadingOverride = useStore((s) => s.setShadingOverride);
  const setSceneShading = useStore((s) => s.setSceneObjectShading);
  const setAutoSmoothAngle = useStore((s) => s.setSceneObjectAutoSmoothAngle);

  const handleShadingChange = (value: ShadingMode) => {
    setShadingOverride(objectId, value);
    if (isSceneObject) setSceneShading(objectId, value);
  };

  const content = (
    <>
      {!bare && <SectionLabel>Appearance</SectionLabel>}
      <EyeToggle
        label="Visible in Render"
        checked={renderVisible}
        onChange={(v) => setRenderVisible(objectId, v)}
      />
      <CompactRow label="Object Shading">
        <SegmentedControl<ShadingMode>
          options={SHADING_OPTIONS}
          value={shading}
          onChange={handleShadingChange}
        />
      </CompactRow>
      {shading === "autoSmooth" && isSceneObject && (
        <SliderRow
          density="compact"
          label="Angle"
          value={autoSmoothAngle}
          min={0}
          max={180}
          displayValue={`${autoSmoothAngle}°`}
          onChange={(v) => setAutoSmoothAngle(objectId, v)}
        />
      )}
      {isSceneObject && <FaceShadingRow objectId={objectId} />}
    </>
  );

  if (bare) return content;

  return <SidebarCard>{content}</SidebarCard>;
}

/**
 * Per-face shading override row — only visible in edit mode with a face
 * selection. Mirrors Blender's `Mesh > Shade Smooth Faces / Shade Flat
 * Faces` operators. The object-wide setting on `AppearanceSection` above
 * remains the default for unmarked faces.
 */
function FaceShadingRow({ objectId }: { objectId: string }) {
  const editMode = useStore((s) => s.editMode);
  const faceAttributes = useStore(
    (s) => s.sceneObjects.get(objectId)?.faceAttributes,
  );
  const setFaceShading = useStore((s) => s.setFaceShading);

  const isEditingThisObject =
    !!editMode && editMode.objectId === objectId && !editMode.isCurve;
  const faceSelectionSize = isEditingThisObject
    ? editMode.selectedFaces.size
    : 0;
  const enabled = faceSelectionSize > 0;
  const selectedFaceShading = useMemo<FaceShadingMode | null>(() => {
    if (!isEditingThisObject || faceSelectionSize === 0) return null;
    let common: FaceShadingMode | null | undefined;
    for (const faceIndex of editMode.selectedFaces) {
      const mode = faceShadingValueToMode(
        getFaceShading(faceAttributes, faceIndex),
      );
      if (mode === null) return null;
      if (common === undefined) {
        common = mode;
        continue;
      }
      if (common !== mode) return null;
    }
    return common ?? null;
  }, [editMode, faceAttributes, faceSelectionSize, isEditingThisObject]);

  if (!isEditingThisObject) return null;

  return (
    <CompactRow label="Face Shading">
      <SegmentedControl<FaceShadingMode>
        options={FACE_SHADING_OPTIONS}
        value={selectedFaceShading}
        onChange={(mode) => setFaceShading(objectId, mode)}
        disabled={!enabled}
      />
    </CompactRow>
  );
}

function faceShadingValueToMode(value: number): FaceShadingMode | null {
  switch (value) {
    case FACE_SHADING_INHERIT:
      return "inherit";
    case FACE_SHADING_FLAT:
      return "flat";
    case FACE_SHADING_SMOOTH:
      return "smooth";
    default:
      return null;
  }
}

export function LightSection({ objectId }: { objectId: string }) {
  const updateLight = useStore((s) => s.updateSceneObjectLight);
  const obj = useStore((s) => s.sceneObjects.get(objectId));
  if (!obj?.light) return null;

  const { color, intensity } = obj.light;
  const kind = obj.objectKind;
  const isDirectional = kind === "directional_light";

  // Point lights use raw intensity; directional uses ×100 for finer UI control.
  const intensityMax = isDirectional ? 300 : 200;
  const toUI = isDirectional ? Math.round(intensity * 100) : intensity;
  const fromUI = (v: number) => (isDirectional ? v / 100 : v);
  const fmt = isDirectional ? intensity.toFixed(2) : intensity.toFixed(0);

  return (
    <SidebarCard style={{ overflow: "hidden" }}>
      <SectionLabel>Light</SectionLabel>
      <CompactRow label="Color">
        <ColorPicker
          value={color}
          onChange={(hex) => updateLight(objectId, { color: hex })}
        />
      </CompactRow>
      <SliderRow
        density="compact"
        label="Intensity"
        value={toUI}
        min={0}
        max={intensityMax}
        displayValue={fmt}
        parseValue={(raw) =>
          isDirectional ? parseFloat(raw) * 100 : parseFloat(raw)
        }
        onChange={(v) => updateLight(objectId, { intensity: fromUI(v) })}
        dimChromeOnDrag
      />
    </SidebarCard>
  );
}

// ---------------------------------------------------------------------------
// MaterialSlotList — Blender-style material slot management for the active object
// ---------------------------------------------------------------------------

function MaterialSlotList({
  objectId,
  slots,
  activeSlotId,
}: {
  objectId: string;
  slots: MaterialSlot[];
  activeSlotId: string | null;
}) {
  const materials = useStore((s) => s.materials);
  const addMaterialSlot = useStore((s) => s.addMaterialSlot);
  const removeMaterialSlot = useStore((s) => s.removeMaterialSlot);
  const setActiveMaterialSlot = useStore((s) => s.setActiveMaterialSlot);
  const renameMaterial = useStore((s) => s.renameMaterial);

  const items = useMemo(
    () =>
      slots.map((slot) => {
        const block = materials.get(slot.materialId);
        return {
          id: slot.id,
          label: block?.name ?? "—",
          // Trailing color swatch — lets users scan slots visually without
          // having to read material names. Uses the new ListControlItem
          // `trailing` slot so the swatch sits right of the label, left of
          // the rename pencil.
          trailing: (
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                flexShrink: 0,
                background: block?.properties.color ?? "#888",
                border: `1px solid ${COLOR_BORDER}`,
              }}
            />
          ),
        };
      }),
    [slots, materials],
  );

  return (
    <ListControl
      items={items}
      activeId={activeSlotId}
      onSelect={setActiveMaterialSlot}
      onAdd={() => addMaterialSlot(objectId)}
      onDelete={(slotId) => removeMaterialSlot(objectId, slotId)}
      onRename={(slotId, name) => {
        // Slots take their label from the referenced material, so renaming a
        // slot is actually a rename of the underlying MaterialDataBlock —
        // matches Blender's single-source-of-truth material naming.
        const slot = slots.find((s) => s.id === slotId);
        if (slot) renameMaterial(slot.materialId, name);
      }}
      canDelete={(xs) => xs.length > 1}
      emptyMessage="No material slots"
      maxHeight={140}
    />
  );
}

// ---------------------------------------------------------------------------
// ActiveSlotEditor — material picker + vertex-group binding for the active slot
// ---------------------------------------------------------------------------

function ActiveSlotEditor({
  objectId,
  slot,
  slotIndex,
}: {
  objectId: string;
  slot: MaterialSlot;
  slotIndex: number;
}) {
  const obj = useStore((s) => s.sceneObjects.get(objectId));
  const materials = useStore((s) => s.materials);
  const sceneObjects = useStore((s) => s.sceneObjects);
  const setMaterialSlotMaterial = useStore((s) => s.setMaterialSlotMaterial);
  const makeUniqueMaterialForSlot = useStore(
    (s) => s.makeUniqueMaterialForSlot,
  );

  // Edit-mode face workflow — Assign / Select / Deselect operate on the
  // current face selection and target this slot index. Mirrors the
  // VertexGroupsPanel button row for muscle-memory consistency.
  const editMode = useStore((s) => s.editMode);
  const isEditingThisObject =
    !!editMode && editMode.objectId === objectId && !editMode.isCurve;
  const faceSelectionSize = isEditingThisObject
    ? editMode.selectedFaces.size
    : 0;
  const assignFacesToMaterialSlot = useStore(
    (s) => s.assignFacesToMaterialSlot,
  );
  const selectFacesByMaterialSlot = useStore(
    (s) => s.selectFacesByMaterialSlot,
  );
  const assignFaceGroupToMaterialSlot = useStore(
    (s) => s.assignFaceGroupToMaterialSlot,
  );

  const faceGroups = obj?.faceAttributes?.groups ?? [];
  const faceGroupOptions = useMemo(
    () =>
      faceGroups
        .filter((g) => g.faces.size > 0)
        .map((g) => ({ value: g.id, label: `${g.name} (${g.faces.size})` })),
    [faceGroups],
  );

  const [galleryOpen, setGalleryOpen] = useState(false);

  // Number of slots (across all objects) referencing this slot's material.
  // Drives the "Make Unique" enable state and the count badge.
  const userCount = useMemo(() => {
    let count = 0;
    for (const o of sceneObjects.values()) {
      for (const s of o.materialSlots)
        if (s.materialId === slot.materialId) count++;
    }
    return count;
  }, [sceneObjects, slot.materialId]);

  // Sorted material options with color swatch icons.
  const matOptions = useMemo(
    () =>
      [...materials.values()]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((m) => ({
          value: m.id,
          label: m.name,
          icon: (
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                flexShrink: 0,
                background: m.properties.color,
                border: `1px solid ${COLOR_BORDER}`,
              }}
            />
          ),
        })),
    [materials],
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        marginBottom: 6,
      }}
    >
      {/* Row 1: material selector + user count badge */}
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <SelectSearch
          value={slot.materialId}
          options={matOptions}
          onChange={(id) =>
            id && setMaterialSlotMaterial(objectId, slot.id, id)
          }
          allowClear={false}
          searchPlaceholder="Search materials…"
        />
        {userCount > 1 && (
          <span
            style={{
              fontSize: FONT_SIZE_TINY,
              color: COLOR_TEXT_FAINT,
              background: COLOR_BORDER,
              borderRadius: RADIUS_DOT,
              padding: "1px 4px",
              lineHeight: 1.2,
              flexShrink: 0,
            }}
          >
            {userCount}
          </span>
        )}
      </div>

      {/* Row 2: Make Unique / Gallery — scoped to the active slot. "New" is
          intentionally omitted because adding a new slot (via ListControl's
          + button) already creates a fresh material as a side effect. */}
      <ButtonControlGroup gap={2}>
        <IconButton
          title="Make single user"
          disabled={userCount <= 1}
          onClick={() => makeUniqueMaterialForSlot(objectId, slot.id)}
          icon={<Unlink size={12} />}
          label="Make single user"
          variant="secondary"
          size="xs"
          style={{ color: COLOR_TEXT_SUBTLE }}
        />
        <IconButton
          title="Open material gallery"
          onClick={() => setGalleryOpen(true)}
          icon={<LibraryBig size={12} />}
          label="Open material gallery"
          variant="secondary"
          size="xs"
          style={{ color: COLOR_TEXT_SUBTLE }}
        />
      </ButtonControlGroup>

      {/* Edit-mode face assignment row — mirrors the Vertex Groups action
          row (Assign / Select / Deselect). Hidden outside edit mode since
          there's no face selection concept then. */}
      {isEditingThisObject && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 4,
            marginTop: 4,
          }}
        >
          <Button
            onClick={() => assignFacesToMaterialSlot(objectId, slotIndex)}
            disabled={faceSelectionSize === 0}
            title="Assign the current face selection to this material slot"
            variant="secondary"
            size="xs"
            fullWidth
          >
            Assign
          </Button>
          <Button
            onClick={() =>
              selectFacesByMaterialSlot(objectId, slotIndex, "add")
            }
            title="Select faces using this material slot"
            variant="secondary"
            size="xs"
            fullWidth
          >
            Select
          </Button>
          <Button
            onClick={() =>
              selectFacesByMaterialSlot(objectId, slotIndex, "subtract")
            }
            title="Deselect faces using this material slot"
            variant="secondary"
            size="xs"
            fullWidth
          >
            Deselect
          </Button>
        </div>
      )}

      {/* Face-group → slot quick-assign. Available in any mode (object or
          edit) so users can drive material assignment from saved face groups
          without manually re-selecting faces. */}
      {faceGroupOptions.length > 0 && (
        <CompactRow label="From Face Group">
          <SelectSearch
            value=""
            options={faceGroupOptions}
            onChange={(groupId) => {
              if (groupId)
                assignFaceGroupToMaterialSlot(objectId, slotIndex, groupId);
            }}
            allowClear={false}
            searchPlaceholder="Search face groups…"
          />
        </CompactRow>
      )}

      <MaterialGalleryModal
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// MaterialSection — slot list + active-slot editor + PBR properties
// ---------------------------------------------------------------------------

export function MaterialSection({
  objectId,
  bare,
}: {
  objectId: string;
  bare?: boolean;
}) {
  const updateMaterial = useStore((s) => s.updateMaterial);
  const obj = useStore((s) => s.sceneObjects.get(objectId));
  const activeMaterialSlotId = useStore((s) => s.activeMaterialSlotId);
  const materials = useStore((s) => s.materials);

  if (!obj) return null;
  const slots = obj.materialSlots;

  // Active slot resolution: top-level transient state; falls back to slot 0
  // whenever the id doesn't resolve (different object selected, slot deleted,
  // etc.). Slot ids are globally unique, so a stale id can never accidentally
  // "match" a slot on a different object.
  const activeSlot =
    slots.find((s) => s.id === activeMaterialSlotId) ?? slots[0];
  const activeSlotIndex = Math.max(
    0,
    slots.findIndex((s) => s.id === activeSlot.id),
  );
  const activeMat = materials.get(activeSlot.materialId)?.properties ?? null;
  if (!activeMat) return null;

  const materialContent = (
    <>
      {!bare && <SectionLabel>Material</SectionLabel>}
      <MaterialSlotList
        objectId={objectId}
        slots={slots}
        activeSlotId={activeSlot.id}
      />
      <div style={{ height: 6 }} />
      <ActiveSlotEditor
        objectId={objectId}
        slot={activeSlot}
        slotIndex={activeSlotIndex}
      />
      <MaterialPropertiesEditor
        properties={activeMat}
        onChange={(patch) => updateMaterial(activeSlot.materialId, patch)}
      />
    </>
  );

  if (bare) return materialContent;

  return (
    <SidebarCard style={{ overflow: "hidden" }}>{materialContent}</SidebarCard>
  );
}
